import { FC, useState } from "react";
import { BrandingViewProps } from "./branding.interfaces";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
} from "@mui/material";
import {
  Edit as EditIcon,
  Upload as UploadIcon,
  Palette as PaletteIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import MainTitle from "../../components/mainTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import { useBrandingHook } from "../../../hooks/useBrandingHook";
import { useInstituteContext } from "../../../context/institute.context";
import LogoUploadDialog from "./logoUploadDialog";
import ColorSchemeDialog from "./colorSchemeDialog";
import BrandingAssetsSection from "./brandingAssetsSection";
import { TBranding } from "../../../apis/branding/branding.interfaces";

const BrandingView: FC<BrandingViewProps> = () => {
  const { selectedInstitute } = useInstituteContext();
  const { data: branding, isLoading, error } = useBrandingHook();
  const [logoUploadDialogOpen, setLogoUploadDialogOpen] = useState(false);
  const [colorSchemeDialogOpen, setColorSchemeDialogOpen] = useState(false);

  const handleLogoUpload = () => {
    setLogoUploadDialogOpen(true);
  };

  const getStatusChip = (status: TBranding["status"]) => {
    const statusConfig = {
      active: { label: "Active", color: "success" as const },
      inactive: { label: "Inactive", color: "default" as const },
      pending: { label: "Pending", color: "warning" as const },
    };

    const config = statusConfig[status] || statusConfig.active;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.branding} />
        <Box p={3} data-testid="branding-view">
          <MainTitle title="Branding" />
          <Alert severity="info">
            Please select an institute from the dashboard to view branding
            configuration.
          </Alert>
        </Box>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.branding} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="branding-view"
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.branding} />
        <Box p={3} data-testid="branding-view">
          <MainTitle title="Branding" />
          <Alert severity="error">
            Failed to load branding configuration. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  const hasBranding =
    branding &&
    (branding.logoUrl || branding.primaryColor || branding.secondaryColor);
  const hasLogo = branding?.logoUrl;

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.branding} />

      <Grid container spacing={3} data-testid="branding-view">
        <Grid item xs={12}>
          <Box sx={{ mb: 2 }}>
            <MainTitle title="Branding Configuration" />
          </Box>
        </Grid>

        {!hasBranding ? (
          // Empty State
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="300px"
                  gap={2}
                >
                  <PaletteIcon sx={{ fontSize: 64, color: "text.secondary" }} />
                  <Typography variant="h6" color="text.secondary">
                    No Branding Configured
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                  >
                    Get started by uploading a logo and configuring your color
                    scheme.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    onClick={handleLogoUpload}
                    size="large"
                    data-testid="upload-logo-empty-button"
                  >
                    Upload Logo
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <>
            {/* Logo Section */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Logo
                  </Typography>
                  {hasLogo ? (
                    <Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={2}
                        sx={{ mb: 2 }}
                      >
                        <Avatar
                          src={branding.logoUrl}
                          alt="Institute Logo"
                          sx={{
                            width: 200,
                            height: 200,
                            bgcolor: "grey.200",
                          }}
                          variant="rounded"
                        />
                        <Typography variant="body2" color="text.secondary">
                          {branding.logoFileName || "Logo"}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={2} justifyContent="center">
                        <Button
                          variant="outlined"
                          startIcon={<UploadIcon />}
                          onClick={handleLogoUpload}
                          size="small"
                          data-testid="replace-logo-button"
                        >
                          Replace
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      gap={2}
                      sx={{ py: 4 }}
                    >
                      <ImageIcon
                        sx={{ fontSize: 64, color: "text.secondary" }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        No logo uploaded
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={handleLogoUpload}
                        size="small"
                        data-testid="upload-logo-button"
                      >
                        Upload Logo
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Color Scheme Section */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="h6">Color Scheme</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setColorSchemeDialogOpen(true)}
                      data-testid="edit-color-scheme-button"
                    >
                      Edit
                    </Button>
                  </Box>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {branding.primaryColor && (
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Primary Color
                        </Typography>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              bgcolor: branding.primaryColor,
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          />
                          <Typography variant="body1">
                            {branding.primaryColor}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {branding.secondaryColor && (
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Secondary Color
                        </Typography>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              bgcolor: branding.secondaryColor,
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          />
                          <Typography variant="body1">
                            {branding.secondaryColor}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {branding.accentColor && (
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Accent Color
                        </Typography>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              bgcolor: branding.accentColor,
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          />
                          <Typography variant="body1">
                            {branding.accentColor}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {!branding.primaryColor &&
                      !branding.secondaryColor &&
                      !branding.accentColor && (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          gap={2}
                          sx={{ py: 4 }}
                        >
                          <PaletteIcon
                            sx={{ fontSize: 64, color: "text.secondary" }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            No color scheme configured
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => setColorSchemeDialogOpen(true)}
                            size="small"
                            data-testid="configure-color-scheme-button"
                          >
                            Configure Color Scheme
                          </Button>
                        </Box>
                      )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Branding Assets Section */}
            <BrandingAssetsSection branding={branding} />

            {/* Branding Status */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h6">Branding Status</Typography>
                    {branding && getStatusChip(branding.status)}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {logoUploadDialogOpen && (
        <LogoUploadDialog
          open={logoUploadDialogOpen}
          onClose={() => setLogoUploadDialogOpen(false)}
          existingLogoUrl={branding?.logoUrl}
        />
      )}

      <ColorSchemeDialog
        open={colorSchemeDialogOpen}
        onClose={() => setColorSchemeDialogOpen(false)}
        branding={branding || undefined}
      />
    </>
  );
};

export default BrandingView;
